import React from 'react';
import { AnimalTrendDataType, AnimalTrendDataTypeTranslations } from '../../models/animal';
import { ReloadIconButton } from '../../elements';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { AnimalTrendDataPoint } from '../../models/animal-trend-data';
import { AnimalSlaughterhouseData, AnimalWeight } from '../../models/animal-view';
import AnimalViewWeightsGraph from '../AnimalViewWeightsGraph/component';
import AnimalViewSlaughterhouse from '../AnimalViewSlaughterhouse/component';
import TrendValueChart from './TrendValueChart';
import TrendNameChart from './TrendNameChart';
import { Card, CardContent, CardHeader } from '../../elements/ui/card';
import { Checkbox } from '../../elements/ui/checkbox';

export interface IProps {
  animalTrendDataType: AnimalTrendDataType;
  trendData: {
    name: string;
    color?: string;
    data: AnimalTrendDataPoint[];
  }[];
  slaughterhouseDataAverage: AnimalSlaughterhouseData | null | undefined;
  weightAverage: AnimalWeight | null | undefined;
  onAnimalTrendDataTypeChanged: (animalTrendDataType: AnimalTrendDataType) => void;
  reloadTrendData?: () => void;
  showPercentage?: boolean;
  onShowPercentageChange?: (value: boolean) => void;
  /** Hides the trend type selector - use when the type is fixed (e.g. previewing a scheduled report) rather than user-selectable. */
  disableTrendTypeSelect?: boolean;
}

export default function TrendDataMulti(props: IProps) {
  const {
    animalTrendDataType,
    trendData,
    slaughterhouseDataAverage,
    weightAverage,
    onAnimalTrendDataTypeChanged,
    reloadTrendData,
    showPercentage = false,
    onShowPercentageChange,
    disableTrendTypeSelect = false
  } = props;
  const { t } = useTranslation();

  const [selectedAnimalTrendDataType, setSelectedAnimalTrendDataType] = React.useState(animalTrendDataType);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: AnimalTrendDataType) => {
    setSelectedAnimalTrendDataType(newValue);
    if (onAnimalTrendDataTypeChanged) {
      onAnimalTrendDataTypeChanged(newValue);
    }
  };

  const useNameAsLabel =
    animalTrendDataType === AnimalTrendDataType.MortalityDisease ||
    animalTrendDataType === AnimalTrendDataType.MortalityReason ||
    animalTrendDataType === AnimalTrendDataType.AntibioticsInjections ||
    animalTrendDataType === AnimalTrendDataType.AntibioticFree;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <InsertChartIcon color="primary" fontSize="large" />
          <h2 className="text-lg font-semibold text-gray-800">{t('f-animal-trend')}</h2>
        </div>
        {reloadTrendData && <ReloadIconButton action={reloadTrendData} />}
      </div>

      {/* Filters */}
      {!disableTrendTypeSelect && (
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-50">
            <AnimalTrendTabs value={selectedAnimalTrendDataType} handleTabChange={handleTabChange} />
          </div>
        </div>
      )}

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">
              {t(AnimalTrendDataTypeTranslations[selectedAnimalTrendDataType])}
            </h3>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 select-none">
              <Checkbox
                checked={showPercentage}
                onCheckedChange={(checked) => onShowPercentageChange?.(checked === true)}
              />
              {t('f-com-show-percentage')}
            </label>
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full aspect-2/1 min-h-62.5 max-h-100">
            {useNameAsLabel ? (
              <TrendNameChart trendData={trendData} showPercentage={showPercentage} />
            ) : (
              <TrendValueChart trendData={trendData} showPercentage={showPercentage} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Average data */}
      {(slaughterhouseDataAverage || weightAverage) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {slaughterhouseDataAverage && <AnimalViewSlaughterhouse slaughterhouseData={slaughterhouseDataAverage} />}
          {weightAverage && <AnimalViewWeightsGraph weight={weightAverage} />}
        </div>
      )}
    </div>
  );
}

export interface AnimalTrendTabsProps {
  value: number;
  handleTabChange: (event: React.ChangeEvent<{}>, newValue: AnimalTrendDataType) => void;
}

export const AnimalTrendTabs: React.FC<any> = ({ value, handleTabChange }: AnimalTrendTabsProps) => {
  const { t } = useTranslation();

  const enumValues = Object.keys(AnimalTrendDataType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => AnimalTrendDataType[key as keyof typeof AnimalTrendDataType]);

  return (
    <FormControl variant="outlined" size="small" fullWidth>
      <InputLabel id="animalTrendTypeLabel">{t('f-com-animal-trend-type')}</InputLabel>
      <Select
        label={t('f-com-animal-trend-type')}
        labelId="animalTrendTypeLabel"
        id="animalTrendTypeSelect"
        value={value}
        onChange={(e) =>
          handleTabChange(e as React.ChangeEvent<{ value: unknown }>, e.target.value as AnimalTrendDataType)
        }
      >
        {enumValues.map((val, index) => (
          <MenuItem key={index} value={val}>
            {t(AnimalTrendDataTypeTranslations[val])}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
